import {
  isAgentMessageChunk,
  isAgentThoughtChunk,
  isToolCall,
  isToolCallProgress,
  isPlan,
  isAvailableCommandsUpdate,
} from '@runloop/remote-agents-sdk/acp';
import type {
  SessionUpdate,
  SessionNotification,
  ContentBlock,
  InitializeResponse,
} from '@runloop/remote-agents-sdk/acp';
import type { AxonEvent } from '@reflex/shared';
import type {
  TurnBlock,
  PlanEntry,
  ACPInitExtensions,
  ACPConfigOption,
  ChatItem,
  AvailableCommand,
  ToolCallBlock,
} from './types.js';
import type { BlockManager } from './useBlockManager.js';
import {
  nextBlockId,
  inferToolKind,
  parseToolCallContent,
  durationSecs,
  UNTITLED_TOOL_CALL,
} from './parsers.js';

/**
 * Handle an ACP protocol event payload.
 * Modeled after the combined-app's handleSessionUpdate + handleTimelineEvent.
 */
export function handleAcpProtocolEvent(
  data: Record<string, unknown>,
  blocks: BlockManager,
  _appendMessage: (msg: ChatItem) => void,
  setIsAgentTurn: (v: boolean) => void,
  event: AxonEvent,
  setAvailableCommands?: (cmds: AvailableCommand[]) => void,
): void {
  const eventType = data.type as string;

  switch (eventType) {
    case 'initialize':
      if (event.origin === 'AGENT_EVENT') {
        handleACPInitialize(data as unknown as InitializeResponse, blocks, event);
      }
      break;
    case 'session/new':
      handleACPSessionNew(data, blocks, event);
      break;
    case 'session/update': {
      const notification = data as unknown as SessionNotification;
      const inner = notification.update;
      if (inner) {
        dispatchSessionUpdate(inner, blocks, setIsAgentTurn, event, setAvailableCommands);
      }
      break;
    }
    case 'agent_message_chunk':
    case 'agent_thought_chunk':
    case 'tool_call':
    case 'tool_call_update':
    case 'plan':
    case 'available_commands_update':
      dispatchSessionUpdate(
        data as unknown as SessionUpdate,
        blocks,
        setIsAgentTurn,
        event,
        setAvailableCommands,
      );
      break;
  }
}

/**
 * Runloop-specific extension fields that appear on ACP initialize/session-new payloads.
 * These are not part of the ACP spec so they are absent from the SDK types.
 */
interface RunloopSessionConfig {
  sessionId?: string;
  modes?:
    | { currentModeId?: string; availableModes?: ACPInitExtensions['modes'] }
    | ACPInitExtensions['modes'];
  models?:
    | { currentModelId?: string; availableModels?: ACPInitExtensions['models'] }
    | ACPInitExtensions['models'];
  configOptions?: ACPConfigOption[];
}

interface RunloopInitializePayload extends InitializeResponse, RunloopSessionConfig {
  clientCapabilities?: Record<string, unknown>;
}

interface ParsedSessionConfig {
  sessionId: string | null;
  currentModeId: string | null;
  currentModelId: string | null;
  modes: ACPInitExtensions['modes'];
  models: ACPInitExtensions['models'];
  configOptions: ACPConfigOption[] | undefined;
  resolvedModel: string | null;
}

function parseSessionConfig(payload: RunloopSessionConfig): ParsedSessionConfig {
  let modes: ACPInitExtensions['modes'];
  let currentModeId: string | null = null;
  if (payload.modes && !Array.isArray(payload.modes)) {
    currentModeId = payload.modes.currentModeId ?? null;
    modes = payload.modes.availableModes;
  } else if (Array.isArray(payload.modes)) {
    modes = payload.modes;
  }

  let models: ACPInitExtensions['models'];
  let currentModelId: string | null = null;
  if (payload.models && !Array.isArray(payload.models)) {
    currentModelId = payload.models.currentModelId ?? null;
    models = payload.models.availableModels;
  } else if (Array.isArray(payload.models)) {
    models = payload.models;
  }

  const sessionId = payload.sessionId ?? null;

  const resolvedModel = currentModelId
    ? (models?.find((m) => m.modelId === currentModelId)?.name ?? currentModelId)
    : models && models.length > 0
      ? (models[0].name ?? models[0].modelId)
      : null;

  return {
    sessionId,
    currentModeId,
    currentModelId,
    modes,
    models,
    configOptions: payload.configOptions,
    resolvedModel,
  };
}

function handleACPInitialize(
  payload: InitializeResponse,
  blocks: BlockManager,
  sourceEvent: AxonEvent,
): void {
  const extended = payload as unknown as RunloopInitializePayload;
  const config = parseSessionConfig(extended);

  const extensions: ACPInitExtensions = {
    protocol: 'acp',
    protocolVersion: payload.protocolVersion ?? null,
    sessionId: config.sessionId,
    currentModeId: config.currentModeId,
    currentModelId: config.currentModelId,
    modes: config.modes,
    models: config.models,
    configOptions: config.configOptions,
    agentCapabilities: payload.agentCapabilities ?? null,
    clientCapabilities: extended.clientCapabilities ?? null,
    authMethods: payload.authMethods ?? undefined,
  };

  if (config.sessionId) blocks.acpEmittedSessionIds.current.add(config.sessionId);

  blocks.pushBlock({
    type: 'system_init',
    id: nextBlockId('init'),
    agentName: payload.agentInfo?.name ?? null,
    agentVersion: payload.agentInfo?.version ?? null,
    model: config.resolvedModel,
    commands: [],
    extensions,
    sourceEvent,
    extra: { ...extended } as Record<string, unknown>,
  });
}

function handleACPSessionNew(
  data: Record<string, unknown>,
  blocks: BlockManager,
  sourceEvent: AxonEvent,
): void {
  const config = parseSessionConfig(data as unknown as RunloopSessionConfig);

  const existingInit = blocks.blocksRef.current.find(
    (b): b is TurnBlock & { type: 'system_init' } => b.type === 'system_init',
  );

  if (existingInit) {
    const prevExt = existingInit.extensions?.protocol === 'acp' ? existingInit.extensions : null;
    const merged: ACPInitExtensions = {
      protocol: 'acp',
      protocolVersion: prevExt?.protocolVersion ?? null,
      sessionId: config.sessionId ?? prevExt?.sessionId ?? null,
      currentModeId: config.currentModeId ?? prevExt?.currentModeId ?? null,
      currentModelId: config.currentModelId ?? prevExt?.currentModelId ?? null,
      modes: config.modes ?? prevExt?.modes,
      models: config.models ?? prevExt?.models,
      configOptions: config.configOptions ?? prevExt?.configOptions,
      agentCapabilities: prevExt?.agentCapabilities ?? null,
      clientCapabilities: prevExt?.clientCapabilities ?? null,
      authMethods: prevExt?.authMethods,
    };

    if (config.sessionId) blocks.acpEmittedSessionIds.current.add(config.sessionId);

    blocks.updateBlocks((prev) =>
      prev.map((b) =>
        b.type === 'system_init' && b.id === existingInit.id
          ? { ...b, model: config.resolvedModel ?? b.model, extensions: merged }
          : b,
      ),
    );
    blocks.noteModel(config.resolvedModel);
  } else if (config.sessionId && blocks.acpEmittedSessionIds.current.has(config.sessionId)) {
    // This session already emitted a system_init block in an earlier turn that
    // has since been flushed. Don't push a duplicate — it would give the new
    // turn its own ownInitBlock, labelling it "Session initialized" instead of
    // "Session details". Just propagate the model switch.
    blocks.noteModel(config.resolvedModel);
  } else {
    if (config.sessionId) blocks.acpEmittedSessionIds.current.add(config.sessionId);

    blocks.pushBlock({
      type: 'system_init',
      id: nextBlockId('init'),
      agentName: null,
      agentVersion: null,
      model: config.resolvedModel,
      commands: [],
      extensions: {
        protocol: 'acp',
        protocolVersion: null,
        sessionId: config.sessionId,
        currentModeId: config.currentModeId,
        currentModelId: config.currentModelId,
        modes: config.modes,
        models: config.models,
        configOptions: config.configOptions,
        agentCapabilities: null,
        clientCapabilities: null,
      },
      sourceEvent,
      extra: { ...data },
    });
    blocks.noteModel(config.resolvedModel);
  }
}

/**
 * Dispatch a typed SessionUpdate using SDK type guards.
 * Matches the combined-app's handleSessionUpdate pattern.
 */
function dispatchSessionUpdate(
  update: SessionUpdate,
  blocks: BlockManager,
  setIsAgentTurn: (v: boolean) => void,
  sourceEvent: AxonEvent,
  setAvailableCommands?: (cmds: AvailableCommand[]) => void,
): void {
  if (isAgentMessageChunk(update)) {
    handleAgentMessageChunk(
      update.content,
      update.messageId ?? null,
      blocks,
      setIsAgentTurn,
      sourceEvent,
    );
    return;
  }

  if (isAgentThoughtChunk(update)) {
    handleAgentThoughtChunk(update.content, blocks, setIsAgentTurn, sourceEvent);
    return;
  }

  if (isToolCall(update)) {
    blocks.finalizeThinking(sourceEvent.timestamp);
    const title = update.title ?? UNTITLED_TOOL_CALL;
    const kind = update.kind ?? inferToolKind(title);
    const status = update.status ?? 'in_progress';
    const locations = (update.locations as ToolCallBlock['locations']) ?? [];
    const contentItems = update.content ? parseToolCallContent(update.content) : [];

    setIsAgentTurn(true);
    blocks.pushBlock({
      type: 'tool_call',
      id: nextBlockId('tc'),
      toolCallId: update.toolCallId ?? '',
      title,
      kind,
      status,
      locations,
      content: contentItems,
      rawInput: (update.rawInput as Record<string, unknown> | null) ?? null,
      rawOutput: (update.rawOutput as Record<string, unknown> | null) ?? null,
      startedAt: sourceEvent.timestamp,
      duration: null,
      sourceEvent,
    });
    return;
  }

  if (isToolCallProgress(update)) {
    const toolCallId = update.toolCallId ?? '';
    const newStatus = update.status ?? undefined;
    // OpenCode (and other ACP agents) frequently emit tool_call_update events that omit a
    // meaningful title/kind — for example, a terminal "completed" update may ship with
    // `title: ""` and `kind: "other"`. Treat empty strings as "not provided" so they do
    // not overwrite the values captured from the initial tool_call event.
    const newTitle = update.title ? update.title : undefined;
    const newKind = update.kind ? update.kind : undefined;
    const newLocations = (update.locations as ToolCallBlock['locations'] | undefined) ?? undefined;
    const newContentItems = update.content ? parseToolCallContent(update.content) : undefined;
    const updateRawInput = update.rawInput as Record<string, unknown> | null | undefined;
    const hasUsefulRawInput = updateRawInput != null && Object.keys(updateRawInput).length > 0;

    blocks.updateBlocks((prev) =>
      prev.map((b) => {
        if (b.type !== 'tool_call' || b.toolCallId !== toolCallId) return b;
        const tc = b;
        // `cancelled` is a sticky terminal status: once the user interrupts a
        // turn, OpenCode still emits a final `tool_call_update` (status
        // `completed`, "User aborted the command") for the aborted tool. Keep
        // the cancelled status — but still merge the update's title/content so
        // the row keeps the richer label/output the completion carries.
        const cancelled = tc.status === 'cancelled';
        const isFinishing =
          !cancelled &&
          (newStatus === 'completed' || newStatus === 'failed') &&
          tc.status !== 'completed' &&
          tc.status !== 'failed';
        return {
          ...tc,
          status: cancelled ? 'cancelled' : (newStatus ?? tc.status),
          title: newTitle ?? tc.title,
          kind: newKind ?? tc.kind,
          locations: newLocations ?? tc.locations,
          content: newContentItems ?? tc.content,
          // Only replace rawInput when the update carries something non-empty — otherwise
          // a completion event with `rawInput: {}` would erase the populated args captured
          // from earlier updates.
          rawInput: hasUsefulRawInput ? updateRawInput : tc.rawInput,
          rawOutput: (update.rawOutput as Record<string, unknown> | null) ?? tc.rawOutput,
          duration: isFinishing ? durationSecs(tc.startedAt, sourceEvent.timestamp) : tc.duration,
        };
      }),
    );
    return;
  }

  if (isPlan(update)) {
    handlePlanUpdate(update.entries as PlanEntry[], blocks, sourceEvent);
    return;
  }

  if (isAvailableCommandsUpdate(update)) {
    if (!setAvailableCommands) return;
    if (Array.isArray(update.availableCommands)) {
      setAvailableCommands(
        update.availableCommands.map((c) => ({
          name: c.name,
          description: c.description,
          input: c.input != null ? Boolean(c.input) : undefined,
        })),
      );
    }
  }
}

function handleAgentMessageChunk(
  content: ContentBlock,
  messageId: string | null,
  blocks: BlockManager,
  setIsAgentTurn: (v: boolean) => void,
  sourceEvent: AxonEvent,
): void {
  setIsAgentTurn(true);
  blocks.finalizeThinking(sourceEvent.timestamp);

  if (content.type === 'resource_link') {
    blocks.pushBlock({
      type: 'resource_link',
      id: nextBlockId('rl'),
      uri: content.uri,
      name: content.name ?? null,
      title: content.title ?? null,
      sourceEvent,
    });
    return;
  }

  if (content.type === 'image') {
    blocks.pushBlock({
      type: 'image',
      id: nextBlockId('img'),
      data: content.data,
      mimeType: content.mimeType,
      uri: content.uri ?? null,
      sourceEvent,
    });
    return;
  }

  if (content.type === 'audio') {
    blocks.pushBlock({
      type: 'audio',
      id: nextBlockId('aud'),
      data: content.data,
      mimeType: content.mimeType,
      sourceEvent,
    });
    return;
  }

  if (content.type === 'resource') {
    const res = content.resource as Record<string, unknown>;
    blocks.pushBlock({
      type: 'resource',
      id: nextBlockId('res'),
      uri: (res.uri as string) ?? '',
      mimeType: (res.mimeType as string) ?? null,
      text: 'text' in res ? (res.text as string) : undefined,
      blob: 'blob' in res ? (res.blob as string) : undefined,
      sourceEvent,
    });
    return;
  }

  const text = content.type === 'text' ? content.text : '';
  const last = blocks.lastBlock();
  if (last?.type === 'text') {
    blocks.updateBlocks((prev) => {
      const copy = [...prev];
      copy[copy.length - 1] = { ...last, text: last.text + text };
      return copy;
    });
  } else {
    blocks.pushBlock({ type: 'text', id: nextBlockId('txt'), text, messageId, sourceEvent });
  }
}

function handleAgentThoughtChunk(
  content: ContentBlock,
  blocks: BlockManager,
  setIsAgentTurn: (v: boolean) => void,
  sourceEvent: AxonEvent,
): void {
  setIsAgentTurn(true);
  const text = content.type === 'text' ? content.text : '';

  const last = blocks.lastBlock();
  if (last?.type === 'thinking' && last.isActive) {
    blocks.updateBlocks((prev) => {
      const copy = [...prev];
      copy[copy.length - 1] = { ...last, text: last.text + text };
      return copy;
    });
  } else {
    if (!blocks.thinkingStartRef.current) blocks.thinkingStartRef.current = sourceEvent.timestamp;
    blocks.pushBlock({
      type: 'thinking',
      id: nextBlockId('think'),
      text,
      duration: null,
      isActive: true,
      sourceEvent,
    });
  }
}

function handlePlanUpdate(
  entries: PlanEntry[],
  blocks: BlockManager,
  sourceEvent: AxonEvent,
): void {
  const planEntries: PlanEntry[] = entries.map((e) => ({
    id: e.id ?? undefined,
    content: e.content,
    status: e.status ?? 'pending',
    priority: e.priority ?? null,
  }));

  blocks.planEntriesRef.current = planEntries;

  const existingPlan = blocks.blocksRef.current.find(
    (b): b is TurnBlock & { type: 'plan' } => b.type === 'plan',
  );
  if (existingPlan) {
    blocks.updateBlocks((prev) =>
      prev.map((b) => (b.type === 'plan' ? { ...b, entries: planEntries } : b)),
    );
  } else {
    blocks.pushBlock({ type: 'plan', id: nextBlockId('plan'), entries: planEntries, sourceEvent });
  }
}
